/**
 * Paid-call reservation for the RapidAPI last resort (StarAPI). One atomic INCR per dispatch on
 * `rapidapi:budget:<YYYY-MM-DD>` (UTC day); once the count passes SOCIAL_STARAPI_DAILY_BUDGET (default
 * 40 000) the call is skipped. If Redis cannot answer, a conservative static cap (2 000 per UTC day,
 * counted in-process) applies instead of "unlimited".
 *
 * 🔴 Browser+edge-safe: shared Upstash client only.
 */
import { redisClient } from '../redis';
import { bounded } from './registry';

export const DEFAULT_PAID_DAILY_BUDGET = 40_000;
export const STATIC_FALLBACK_CAP = 2_000;
const KEY_TTL_S = 48 * 60 * 60;
const REDIS_WAIT_MS = 250;

const utcDay = (d: Date): string => d.toISOString().slice(0, 10);
export const paidQuotaKey = (d: Date = new Date()): string => `rapidapi:budget:${utcDay(d)}`;

/** Static read (edge inlining). Unset / invalid → 40 000. Deliberately NOT STARAPI_DAILY_BUDGET: that name already
 *  belongs to starapi-budget.ts (a per-app tier-shedding budget, default 3300) and must not move this global cap. */
export function paidDailyBudget(): number {
	const parsed = parseInt(process.env.SOCIAL_STARAPI_DAILY_BUDGET || '', 10);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PAID_DAILY_BUDGET;
}

let local = { day: '', count: 0 };
export function __resetPaidQuota(): void {
	local = { day: '', count: 0 };
}

export interface Reservation {
	ok: boolean;
	count: number;
	cap: number;
	source: 'redis' | 'static';
}

/** Reserve one paid call. Never throws. */
export async function reservePaidCall(now: Date = new Date()): Promise<Reservation> {
	const cap = paidDailyBudget();
	const key = paidQuotaKey(now);
	try {
		const count = Number(await bounded(redisClient.incr(key), REDIS_WAIT_MS));
		if (!Number.isFinite(count)) throw new Error('INCR returned a non-number');
		if (count === 1) {
			try { void Promise.resolve(redisClient.expire(key, KEY_TTL_S)).catch(() => {}); } catch { /* best effort */ }
		}
		return { ok: count <= cap, count, cap, source: 'redis' };
	} catch (error: any) {
		const day = utcDay(now);
		if (local.day !== day) local = { day, count: 0 };
		local.count += 1;
		console.error(`[router/quota] redis unavailable (${error?.message || error}); static cap ${STATIC_FALLBACK_CAP}/day in force (${local.count} used in this isolate)`);
		return { ok: local.count <= STATIC_FALLBACK_CAP, count: local.count, cap: STATIC_FALLBACK_CAP, source: 'static' };
	}
}

/** Give a reservation back (the attempt was aborted before anything was dispatched). Never throws. */
export async function refundPaidCall(reservation: Reservation, now: Date = new Date()): Promise<void> {
	try {
		if (reservation.source === 'redis') await bounded(redisClient.decr(paidQuotaKey(now)), REDIS_WAIT_MS);
		else if (local.day === utcDay(now) && local.count > 0) local.count -= 1;
	} catch { /* best effort: a lost refund only makes the cap more conservative */ }
}
